import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/api';

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    const authToken = localStorage.getItem('authToken');

    if (!storedUserData || !authToken) {
      navigate('/login');
      return;
    }

    setUserData(JSON.parse(storedUserData));

    const fetchEventTypes = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.eventTypes.getAll());
        const data = await response.json();
        setEventTypes(data);
      } catch (error) {
        console.error('Etkinlik türleri yüklenirken hata:', error);
      }
    };

    const fetchUserEvents = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.event.getByUserEvent(), {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        const data = await response.json();

        if (response.ok) {
          setEvents(data);
        } else {
          throw new Error(data.error || 'Etkinlikler yüklenirken bir hata oluştu');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEventTypes();
    fetchUserEvents();
  }, [navigate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredEvents = selectedType
    ? events.filter(event => event.event_type_id === selectedType)
    : events;

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {userData && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar src={userData.picture} alt={userData.name} sx={{ mr: 2 }} />
            <Typography variant="h6">Hoş geldin, {userData.name}</Typography>
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 3,
          gap: 2
        }}>
          <Typography variant="h4" component="h1" sx={{ mb: { xs: 2, sm: 0 } }}>
            Etkinliklerim
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2,
            width: { xs: '100%', sm: 'auto' } 
          }}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Etkinlik Türü</InputLabel>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                label="Etkinlik Türü"
              >
                <MenuItem value="">Tümü</MenuItem>
                {eventTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              variant="contained" 
              onClick={() => navigate('/event/create')}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              YENİ ETKİNLİK
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {filteredEvents.map((event) => (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 }
                }}
                onClick={() => navigate(`/event/${event.code_hash}`)}
              >
                {event.image_url && (
                  <Box
                    component="img"
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      mb: 2,
                      borderRadius: 1
                    }}
                    src={event.image_url}
                    alt={event.name}
                  />
                )}
                <Typography variant="h6" gutterBottom>
                  {event.name}
                </Typography>
                <Typography variant="caption" gutterBottom>
                  {event.description}
                </Typography>
                {event.event_type_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    {event.event_type_name}
                  </Typography>
                )}
                <Typography color="text.secondary">
                  {new Date(event.event_date).toLocaleDateString()}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Dashboard;
